import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Popover, Typography } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { FileField, RequestFile } from "models/interfaces";
import moment from "moment";
import React, { useRef, useState } from "react";
import {
  updateFieldInSignRequest,
  updateFieldOfSelectedFile,
} from "redux/appSlice";

const DateField = ({
  field,
  dispatch,
  coords,
  isMobile,
}: {
  field: FileField;
  dispatch?: Function;
  selectedFile?: RequestFile;
  coords: any;
  isMobile?: boolean;
}) => {
  const format = field.dateFormat || "MM/DD/YYYY";
  const display = field.value || format;
  const isPlaceholder = !field.value;
  const styleSize = field.styles?.size ?? 16;
  const fontSize = Math.min(coords.pageH - 1, styleSize) || styleSize;
  const iconSize = Math.max(12, Math.min(coords.pageH * 0.8, 18));

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const handleSelect = (newValue: Date | null) => {
    if (view === "day") {
      setOpen(false);
    }
    if (!newValue || !moment(newValue).isValid()) return;
    const formatted = moment(newValue).format(format);
    if (dispatch) {
      dispatch(
        updateFieldOfSelectedFile({
          id: field.id,
          value: formatted,
          dateFormat: format,
        }),
      );
      dispatch(
        updateFieldInSignRequest({
          id: field.id,
          value: formatted,
          dateFormat: format,
        }),
      );
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    if (!dispatch) return;
    dispatch(updateFieldOfSelectedFile({ id: field.id, value: "" }));
    dispatch(updateFieldInSignRequest({ id: field.id, value: "" }));
  };

  const parseDate = React.useMemo(() => {
    return field.value && moment(field.value, format).isValid()
      ? moment(field.value, format).toDate()
      : null;
  }, [field.value, format]);

  const [view, setView] = React.useState<"year" | "month" | "day">("day");

  const handleYearChange = (date: Date) => {
    setView("month");
  };

  const handleMonthChange = (date: Date) => {
    setView("day");
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "4px",
        px: "4px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize,
          color: isPlaceholder ? "#9CA3AF" : "#1A1A1A",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          userSelect: "none",
        }}
      >
        {display}
      </Typography>
      {!!field.value && (
        <IconButton
          className="no-drag"
          size="small"
          onClick={handleClear}
          onMouseDown={(e) => e.stopPropagation()}
          title="Clear date"
          sx={{
            flex: "0 0 auto",
            padding: 0,
            color: "#646464",
          }}
        >
          <CloseIcon sx={{ fontSize: iconSize }} />
        </IconButton>
      )}
      <IconButton
        ref={anchorRef}
        className="no-drag"
        size="small"
        aria-label="Open date picker"
        onClick={(e) => {
          e.stopPropagation();
          setView("day");
          setOpen(true);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          flex: "0 0 auto",
          padding: 0,
          color: "#646464",
        }}
      >
        <CalendarMonthIcon sx={{ fontSize: iconSize }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateCalendar
            value={parseDate}
            onChange={handleSelect}
            views={["year", "month", "day"]}
            view={view}
            onViewChange={(newView) => setView(newView)}
            onYearChange={handleYearChange}
            onMonthChange={handleMonthChange}
          />
        </LocalizationProvider>
      </Popover>
    </Box>
  );
};

export default DateField;
